/**
 * client/src/pages/security-centre/components/ProtectionLevelSummary.tsx
 *
 * Provides the AAL-specific explanation and step-up action used by the
 * route-level AAL gate. Security Overview intentionally does not mount this
 * component: its posture guidance is semantic and server-assessed instead.
 *
 * Four parts, in the order a person asks for them:
 *  1. The CURRENT level, stated plainly, with the one-line reason it is that
 *     level. Always visible — the reason is the whole point of the card, so
 *     it is never hidden behind a disclosure.
 *  2. What that level ADMITS right now, and (below Level 3) what it does not.
 *     Both are read off the same `LEVEL_GRANTS` table, so the "locked" list
 *     can never drift from the level that would unlock it.
 *  3. ONE primary next-step action — the cheapest LIVE route, resolved by
 *     `resolveProtectionNextStep` (server-precedence-ordered, see that
 *     module's header) over `useAal3RouteStatus`'s three-valued reads. Never
 *     guesses: an unresolved read renders nothing rather than a wrong or
 *     premature action. When the two-passkey check is ALSO live alongside a
 *     certified key, it renders once, here, as a secondary explicitly-chosen
 *     option (ledger #86) — never a second competing primary.
 *  4. A collapsed "every level" disclosure (DESIGN-SYSTEM §7.1 rule 1 — plain
 *     term on screen, detail available) for the full ladder.
 *
 * The "Verify" actions reuse `useStepUpAAL3` DIRECTLY (the same "machinery"
 * `Aal3ElevationNotice` drives) rather than embedding that notice component
 * wholesale — its own idle button label is fixed to the hardware wording
 * regardless of which basis will run, which would misdescribe the two-key
 * route this panel already knows is the one about to fire (Friction Rule
 * clause 2: a label must describe what a tap actually does).
 *
 * HONEST FRAMING (Friction Rule clause 2, CLAUDE.md §AAL Thresholds): every
 * level description below states ENROLMENT/CAPABILITY facts only — never a
 * claim that a live human is present, or that deepfakes are resisted.
 * "Level 3 of 4" is proven fresh at the moment of verification and
 * lasts for the CURRENT SESSION only — stated explicitly rather than left
 * implicit, since a level reads as a permanent state if the session
 * boundary isn't named. The reason lines likewise name what a session
 * PROVED (a passkey, a certified key, a memory baseline), never who was
 * holding it, and never that a ceremony ran when a sign-in may have sufficed.
 */

import { useCallback, useState } from "react";
import { Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Lock } from "lucide-react";
import { CollapsibleSection } from "@/components/ui/collapsible-section";
import { MilestonePill } from "@/components/premium";
import { protectionLevelDisplay, PROTECTION_LEVELS } from "@/lib/terminology";
import { useAal3RouteStatus } from "@/app/aal3-route-status";
import { useStepUpAAL3 } from "@/lib/use-step-up-aal3";
import { stepUpRunningLabel } from "@/lib/use-step-up-aal3-types";
import { AUTH_ME_QUERY_KEY } from "@/lib/auth-me";
import { formatRemainingTime } from "@/lib/format-timestamp";
import { ADD_SECOND_PASSKEY_HREF } from "@/lib/add-key-links";
import { MasterHeldNextStep } from "./MasterHeldNextStep";
import { resolveProtectionNextStep } from "../lib/protection-next-step";
import {
  LEVEL_GRANTS,
  LEVEL_ORDER,
  LEVEL_TONE,
  levelReason,
  lockedGrants,
  lockedRouteNote,
  hardwareCheckPendingNote,
  type Aal,
} from "../lib/protection-level-copy";

export interface ProtectionLevelSummaryProps {
  aal: Aal;
}

export function ProtectionLevelSummary({ aal }: ProtectionLevelSummaryProps) {
  const queryClient = useQueryClient();
  const isMaximum = aal === "AAL3";
  // Only fetch the route facts while there's a next step to find.
  const routes = useAal3RouteStatus(!isMaximum);
  // An AAL1 session's next level is Level 2, not Level 3 — and for a durably
  // downgraded account (OSINT reset, recovery completion) a fresh login
  // re-caps to AAL1, so the ONLY non-looping remedy is the AAL2 step-up
  // ceremony. Same hook, parameterised (`use-step-up-aal3.ts` takes the
  // target), never a second ceremony implementation.
  const { runStepUp, isRunning, phase, error } = useStepUpAAL3(aal === "AAL1" ? "AAL2" : "AAL3");
  // Which basis the in-flight run was started with, so the running label
  // renders inside the button that was actually tapped ("dual" = the
  // secondary two-passkey option, ledger #86). Display state only.
  const [runningBasis, setRunningBasis] = useState<"default" | "dual">("default");

  const handleVerify = useCallback((basis?: "dual") => {
    setRunningBasis(basis === "dual" ? "dual" : "default");
    runStepUp(basis === "dual" ? { basis } : undefined)
      .then(() => {
        // The server has already promoted session.aal by the time this
        // resolves (completeStepUp's side effect) — refresh the cached
        // session so the card's own "current level" updates in place.
        void queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
      })
      .catch(() => {
        // Recoverable — `error` (below) drives the UI; nothing further here.
      });
  }, [runStepUp, queryClient]);

  // The AAL3 route resolution is meaningless at AAL1 — that session has to
  // reach Level 2, fresh first, and offering it a two-key ceremony would
  // skip a level (and, at `hardware: "none"`, offer a ceremony it cannot run).
  const nextStep = isMaximum || aal === "AAL1"
    ? null
    : resolveProtectionNextStep(routes);
  // AAL1 with a credential on file has a real, runnable remedy; AAL1 with
  // none has nothing to verify and is sent to register instead.
  const showAal2Verify = aal === "AAL1" && routes.hardware !== "none" && routes.hardware !== "unknown";

  // ONE label decides whether a verify button renders at all, and what it
  // says — so the two arms cannot disagree about which ceremony is about to
  // run (Friction Rule clause 2: a label must describe what a tap does).
  const verifyLabel: string | null =
    showAal2Verify                        ? "Verify with your passkey" :
    nextStep?.kind === "verify-hardware"  ? "Verify with your security key" :
    nextStep?.kind === "verify-two-key"   ? "Verify with two of your passkeys" :
    null;

  // Null unless a credential's registry check is genuinely still outstanding.
  const checkPendingNote = isMaximum ? null : hardwareCheckPendingNote(routes.hardware);

  return (
    <div data-testid="protection-level-summary" className="space-y-3">
      {/* 1 + 2 — the current level, why it is that level, and what it admits.
          Always visible: this is the answer the card exists to give. */}
      <div className="rounded-[var(--radius-md)] border border-border-subtle bg-bg-overlay/40 px-3.5 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span
            data-testid="protection-current-level"
            className="text-[length:var(--text-body-size)] font-semibold text-text-primary"
          >
            {/* Owner ruling 2026-09-01: standalone status headline, hybrid "Level N of 4 · <name>" form. */}
            {protectionLevelDisplay(aal)}
          </span>
          <MilestonePill tone={LEVEL_TONE[aal]}>Right now</MilestonePill>
        </div>

        <p data-testid="protection-current-reason" className="mt-1 text-[length:var(--text-caption-size)] text-text-secondary">
          {levelReason(aal, routes)}
        </p>

        <p
          data-testid="protection-current-grants"
          className="mt-2 flex items-start gap-1.5 text-[length:var(--text-caption-size)] text-text-secondary"
        >
          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-400" aria-hidden="true" />
          <span>
            <span className="font-medium text-text-primary">You can: </span>
            {LEVEL_GRANTS[aal]}
          </span>
        </p>

        {!isMaximum && (
          <p
            data-testid="protection-current-locked"
            className="mt-1.5 flex items-start gap-1.5 text-[length:var(--text-caption-size)] text-text-tertiary"
          >
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-text-tertiary" aria-hidden="true" />
            <span>
              <span className="font-medium text-text-secondary">Not yet: </span>
              {lockedGrants(aal).join(" ")} {lockedRouteNote(aal, nextStep, routes)}
            </span>
          </p>
        )}

        {/* An unestablished certification is stated as unestablished — never
            folded into the "not certified" reading every other line here takes
            off the same flag. See hardwareCheckPendingNote. */}
        {checkPendingNote !== null && (
          <p
            data-testid="protection-hardware-check-pending"
            className="mt-1.5 text-[length:var(--text-caption-size)] text-text-tertiary"
          >
            {checkPendingNote}
          </p>
        )}

        {/* 3 — the ONE route out of here.
            Both arms (AAL1's Level-2-fresh step-up and the AAL3 routes)
            render through a single block: they share the hook, the handler,
            the disabled state, the error alert and the testids, so keeping
            two copies only let them drift — the AAL1 copy had already lost
            the two-leg running-label progression. */}
        {(verifyLabel !== null || nextStep) && (
          <div data-testid="protection-next-step" className="mt-2.5 text-[length:var(--text-caption-size)]">
            {nextStep?.kind === "add-second-passkey" && (
              <Link
                href={ADD_SECOND_PASSKEY_HREF}
                data-testid="protection-next-step-add-device"
                className="text-interactive hover:underline"
              >
                {/* "passkey", not "device": this link registers a passkey on the
                    CURRENT machine, and two passkeys can legitimately live on one
                    physical device (ledger #71(b) names that bound honestly). */}
                Add a second passkey
              </Link>
            )}

            {nextStep?.kind === "master-held" && <MasterHeldNextStep />}

            {nextStep?.kind === "two-key-waiting" && (
              <p data-testid="protection-next-step-waiting" className="text-text-secondary m-0">
                Your newest passkey can be used for this{" "}
                {nextStep.matureAt
                  ? `in ${formatRemainingTime(nextStep.matureAt.getTime())}`
                  : "once it's been on your account for 24 hours"}
                .
              </p>
            )}

            {verifyLabel !== null && (
              <div className="flex flex-col items-start gap-2">
                {error !== null && (
                  <p role="alert" data-testid="protection-next-step-error" className="text-warning m-0">
                    {error}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => handleVerify()}
                  disabled={isRunning}
                  data-testid="protection-next-step-verify"
                  className="min-h-[44px] inline-flex items-center px-3 rounded-[var(--radius-md)] border border-border-default text-text-primary font-semibold hover:border-interactive transition-colors duration-[var(--duration-fast)] disabled:opacity-40 disabled:pointer-events-none"
                >
                  {isRunning && runningBasis === "default" ? stepUpRunningLabel(phase) : verifyLabel}
                </button>
                {/* Secondary, explicitly-chosen route (ledger #86): shown ONLY
                    when the two-passkey check is genuinely live alongside the
                    certified key — e.g. the key isn't in hand right now. A
                    second path to the same outcome, kept because it has a
                    different physical prerequisite (DESIGN-SYSTEM §7.3 rule 4).
                    It maps to an explicit basis:"dual" request the server
                    validates — the label always names what the tap runs.
                    AC-84 (Task 10, fix round 1): `data-secondary-cta` is what
                    keeps this OUT of the single-CTA count — every OTHER
                    interactive element in `protection-next-step` counts as
                    primary by default (structural rule, see
                    tests/unit/protection-level-summary-single-cta.test.tsx),
                    so a future competing button added here without this
                    attribute is caught, not silently exempted. */}
                {nextStep?.kind === "verify-hardware" && nextStep.twoKeyAlso && (
                  <button
                    type="button"
                    onClick={() => handleVerify("dual")}
                    disabled={isRunning}
                    data-testid="protection-next-step-verify-two-key"
                    data-secondary-cta="true"
                    className="min-h-[44px] inline-flex items-center text-interactive hover:underline disabled:opacity-40 disabled:pointer-events-none"
                  >
                    {isRunning && runningBasis === "dual"
                      ? stepUpRunningLabel(phase)
                      : "No key with you? Use two of your passkeys instead"}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 4 — the full ladder, collapsed. */}
      <CollapsibleSection id="protection-grants" title="What every level unlocks" defaultOpen={false}>
        <ul className="space-y-2.5 pb-1" data-testid="protection-grants-list">
          {LEVEL_ORDER.map((level) => {
            const isCurrent = level === aal;
            return (
              <li key={level} data-testid={`protection-grant-${level}`} data-current={isCurrent}>
                <div className="flex items-center gap-2">
                  <span className="text-[length:var(--text-body-size)] font-semibold text-text-primary">
                    {/* Owner ruling 2026-09-01: hybrid form, same as the current-level line above. */}
                    {protectionLevelDisplay(level)}
                  </span>
                  {isCurrent && (
                    <MilestonePill tone={LEVEL_TONE[level]}>
                      <span data-testid={`protection-grant-${level}-current`}>You are here</span>
                    </MilestonePill>
                  )}
                </div>
                <p className="text-[length:var(--text-caption-size)] text-text-tertiary m-0">
                  {LEVEL_GRANTS[level]}
                </p>
              </li>
            );
          })}
        </ul>
        <p
          data-testid="protection-session-boundary"
          className="text-[10px] text-text-tertiary m-0"
        >
          {PROTECTION_LEVELS.AAL3.label} is proven fresh each time you verify, and lasts for this
          session only — it is never a permanent state.
        </p>
      </CollapsibleSection>
    </div>
  );
}
